import UnsavedChangesModal from '@brands/Dashboard/Dashboard/Components/UnsavedChangesModal/UnsavedChangesModal';
import Container from '@brands/Components/Container/Container';
import { useWindowSize } from '@brands/hooks';
import { useUnsavedChangesWarning } from '@brands/hooks/useUnsavedChangesWarning';
import { listAllClientTags } from '@brands/services/identity/listAllClientTags';
import { scopeClientTagOptionsToActor } from '@brands/services/identity/clientTagActorScope';
import { ClientTag, UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { defaultUserValues } from '@brands/services/cases/types/defaultUserValues';
import { displayErrorDetails } from '@brands/Utils/displayError';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Loading } from '../../../Components/LoadingSpinner/Loading';
import { useAppSelector } from '../../../hooks/useReduxHook';
import { getCurrentProfile } from '../../../services/identity/getCurrentProfile';
import { getUserById } from '../../../services/identity/getUserById';
import { selectAuth } from '@brands/store/selectors/auth';
import ChangeUserStatusModal from '../Modal/ChangeUserStatusModal';
import ResetPasswordModal from '../Modal/ResetPasswordModal';
import CollectorProfile, { ClientTagOption, mapUserToCollectorForm } from './Profile';
import { CollectorMyProfileSubmit } from './utils/types';
import styles from '../styles.module.scss';

export type CollectorMyProfileProps = {
  reviewProfile?: boolean;
};

const CollectorMyProfile: React.FC<CollectorMyProfileProps> = ({ reviewProfile = false }) => {
  const screenSize = useWindowSize();
  const formRef = useRef<HTMLFormElement>(null);
  const userIdParam = useParams().userId as string | undefined;
  const { userInfo } = useAppSelector(selectAuth);

  const [loading, setLoading] = useState(true);
  const [, setCurrentAvatarURL] = useState('');
  const [thisUser, setThisUser] = useState<UserProfile>(defaultUserValues);
  const [myProfileForm, setMyProfileForm] = useState<CollectorMyProfileSubmit | null>(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [clientTagsLoaded, setClientTagsLoaded] = useState(false);
  const [profileError, setProfileError] = useState(false);
  const [clientTagOptions, setClientTagOptions] = useState<ClientTagOption[]>([]);
  const [clientTags, setClientTags] = useState<ClientTag[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetPasswordConfirmation, setResetPasswordConfirmation] = useState<{
    isOpen: boolean;
    userState: string;
  }>({ isOpen: false, userState: '' });
  const [changeUserStatusFlag, setChangeUserStatusFlag] = useState('');

  const { showModal, confirmNavigation, saveChanges, hideModal } = useUnsavedChangesWarning(isDirty, formRef);

  useEffect(() => {
    listAllClientTags(reviewProfile ? undefined : userInfo.role.name)
      .then(({ nodes }) => {
        const mapped = (nodes || []).map((c) => ({ id: String(c.id), value: c.id, label: c.name }));
        setClientTagOptions(scopeClientTagOptionsToActor(mapped, userInfo));
        setClientTags(nodes || []);
        setClientTagsLoaded(true);
      })
      .catch(() => {
        setClientTagOptions([]);
        setClientTags([]);
        setClientTagsLoaded(true);
      });
  }, [reviewProfile, userInfo.role.name]);

  useEffect(() => {
    setUserLoaded(false);
    setProfileError(false);
    setMyProfileForm(null);
    setLoading(true);

    const fetchUser = async (): Promise<void> => {
      try {
        if (reviewProfile && userIdParam) {
          const user = await getUserById(Number(userIdParam), userInfo.role.name);
          setThisUser(user as UserProfile);
        } else {
          const user = await getCurrentProfile(userInfo.role.name);
          setThisUser(user as UserProfile);
        }
      } catch (e: unknown) {
        displayErrorDetails(e);
        setProfileError(true);
      } finally {
        setUserLoaded(true);
      }
    };
    void fetchUser();
  }, [reviewProfile, userIdParam, userInfo.role.name]);

  useEffect(() => {
    if (!userLoaded || !clientTagsLoaded || profileError) return;
    if (!thisUser?.id) return;
    setMyProfileForm(mapUserToCollectorForm(thisUser));
    setLoading(false);
    if (reviewProfile) {
      setResetPasswordConfirmation((p) => ({ ...p, userState: thisUser.state || '' }));
    }
  }, [userLoaded, clientTagsLoaded, profileError, thisUser, reviewProfile]);

  const disableResetButton = (): void => {
    setResetEmailSent(true);
  };

  const disableDeleteButton = (): void => {
    setThisUser((prev) => ({ ...prev, status: 'Deactivated' }));
  };

  const profileBody = (
    <>
      <div className={styles.myProfileContainer}>
        {myProfileForm && (
          <CollectorProfile
            formRef={formRef}
            setIsDirty={setIsDirty}
            myProfileForm={myProfileForm}
            setMyProfileForm={setMyProfileForm}
            thisUser={thisUser}
            setThisUser={setThisUser}
            setCurrentAvatarURL={setCurrentAvatarURL}
            clientTagOptions={clientTagOptions}
            clientTagCatalog={clientTags}
            reviewProfile={reviewProfile}
            resetEmailSent={resetEmailSent}
            setResetPasswordConfirmation={setResetPasswordConfirmation}
            setChangeUserStatusFlag={setChangeUserStatusFlag}
            userId={userIdParam}
          />
        )}

        {showModal && (
          <UnsavedChangesModal
            isOpen={showModal}
            onConfirm={confirmNavigation}
            saveChange={saveChanges}
            loading={false}
            hideModal={hideModal}
          />
        )}
      </div>
      {reviewProfile && resetPasswordConfirmation.isOpen && userIdParam && (
        <ResetPasswordModal
          setOpenModal={setResetPasswordConfirmation}
          disableButton={disableResetButton}
          userId={Number(userIdParam)}
          userEmail={thisUser.email}
          userName={thisUser.full_name}
          isDeactivated={thisUser.status === 'Deactivated'}
          userState={resetPasswordConfirmation.userState}
        />
      )}
      {reviewProfile && changeUserStatusFlag && userIdParam && (
        <ChangeUserStatusModal
          setOpenModal={setChangeUserStatusFlag}
          disableButton={disableDeleteButton}
          userId={Number(userIdParam)}
          userName={thisUser.full_name}
          status={changeUserStatusFlag}
        />
      )}
    </>
  );

  if (profileError) {
    return (
      <Container patientDashboard childClassName={styles.dashboard} isSidenavVisible={!reviewProfile}>
        <div className={styles.myProfileContainer} style={{ padding: 24 }}>
          Unable to load {reviewProfile ? 'this user' : 'your profile'}. Please refresh the page.
        </div>
      </Container>
    );
  }

  if (loading || !myProfileForm?.collectorId) {
    return <Loading fullScreen />;
  }

  if (reviewProfile) {
    return <>{profileBody}</>;
  }

  return (
    <Container
      patientDashboard
      childClassName={styles.dashboard}
      containerStyle={{
        justifyContent: screenSize.width >= 1289 ? 'center' : 'start',
        overflowY: screenSize.width >= 1289 ? 'hidden' : 'scroll',
      }}
    >
      {profileBody}
    </Container>
  );
};

export default CollectorMyProfile;
